import { DocumentReference, ISoftDelete } from '@principle-theorem/shared';
import { IOrganisation } from './organisation/organisation';
import { IPractice } from './practice/practice';

export interface IApiKey extends ISoftDelete {
  label: string;
  tokenUid: string;
  apiKey: string;
  allowedPractices: DocumentReference<IPractice>[];
  allowedScopes: ApiScope[];
  /**
   * Management-set override for how call events created with this key are
   * labelled on timelines & transcripts. When blank/absent, consumers fall back
   * to the key's own `label`, then 'AI Receptionist'. The `createdBy.name`
   * snapshot on events is never used for display.
   */
  sourceLabel?: string;
}

export interface IAddApiKeyRequest {
  label: string;
  organisationRef: DocumentReference<IOrganisation>;
  allowedPractices: DocumentReference<IPractice>[];
  allowedScopes: ApiScope[];
  sourceLabel?: string;
}

export type AddApiKeyResponse = Pick<IApiKey, 'label' | 'apiKey' | 'tokenUid'>;

export enum ApiScope {
  PatientsRead = 'patients:read',
  PatientsWrite = 'patients:write',
  AppointmentsRead = 'appointments:read',
  AppointmentsWrite = 'appointments:write',
  AccountsRead = 'accounts:read',
  AccountsWrite = 'accounts:write',
  FileRead = 'files:read',
  FileWrite = 'files:write',
  MessagesRead = 'messages:read',
  MessagesWrite = 'messages:write',
  InteractionsWrite = 'interactions:write',
  NotificationsWrite = 'notifications:write',
  EmailsRead = 'emails:read',
  EmailsWrite = 'emails:write',
  TagsRead = 'tags:read',
  TagsWrite = 'tags:write',
  PracticesRead = 'practices:read',
  AvailabilityRead = 'availability:read',
  TreatmentPlansRead = 'treatment-plans:read',
  TreatmentPlansWrite = 'treatment-plans:write',
}

export const API_SCOPE_DESCRIPTIONS: Record<ApiScope, string> = {
  [ApiScope.PatientsRead]: 'Read patient data',
  [ApiScope.PatientsWrite]: 'Write patient data',
  [ApiScope.AppointmentsRead]:
    'Read appointment data including online booking requests',
  [ApiScope.AppointmentsWrite]:
    'Write appointment data including online booking requests',
  [ApiScope.AccountsRead]:
    'Read account data including invoices/transactions/deposits',
  [ApiScope.AccountsWrite]:
    'Write account data including invoices/transactions/deposits',
  [ApiScope.FileRead]: 'Read patient file data',
  [ApiScope.FileWrite]: 'Write patient file data',
  [ApiScope.MessagesRead]: 'Read SMS message data',
  [ApiScope.MessagesWrite]: 'Write SMS message data',
  [ApiScope.InteractionsWrite]: 'Write patient/appointment interaction data',
  [ApiScope.EmailsRead]: 'Read email message data',
  [ApiScope.EmailsWrite]: 'Write email message data',
  [ApiScope.NotificationsWrite]: 'Write notification data',
  [ApiScope.TagsRead]: 'Read tag data',
  [ApiScope.TagsWrite]: 'Write tag data',
  [ApiScope.PracticesRead]: 'Read practice data',
  [ApiScope.AvailabilityRead]:
    'Read availability data including associated data for searching appointment suggestions',
  [ApiScope.TreatmentPlansRead]: 'Read treatment plans, steps, and treatments',
  [ApiScope.TreatmentPlansWrite]:
    'Write treatment plans, steps, and treatments',
};

export function convertApiScopeToDescription(scope: ApiScope): string {
  return API_SCOPE_DESCRIPTIONS[scope];
}
